import React, { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { TransitionGroup, CSSTransition } from 'react-transition-group';

import bp from '../assets/js/breakpoints';
import { createTranslation } from '../assets/js/utilities';

import EventsListView from './EventsListView';
import EventsMonthView from './EventsMonthView';

const Calendar = ({ events, locale }) => {
  const [currentView, setCurrentView] = useState('list');
  const [selectedFilters, setSelectedFilters] = useState({});
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize filters from URL query params on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const filterParams = params.getAll('filter');

    if (filterParams.length > 0) {
      const filtersObj = {};
      filterParams.forEach(filter => {
        filtersObj[filter] = true;
      });
      setSelectedFilters(filtersObj);
    }
    // Mark as initialized regardless of whether URL had params
    setIsInitialized(true);
  }, []);

  // Update URL when filters change - but skip the first update if we just loaded from URL
  useEffect(() => {
    if (!isInitialized) return;

    const activeFilters = Object.keys(selectedFilters).filter(
      key => selectedFilters[key]
    );
    const params = new URLSearchParams(window.location.search);

    // Remove all existing filter params
    params.delete('filter');

    // Add new filter params
    activeFilters.forEach(filter => {
      params.append('filter', filter);
    });

    const newUrl = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;
    window.history.replaceState({}, '', newUrl);
  }, [selectedFilters, isInitialized]);

  // Extract unique tags from events that have them
  const availableFilters = useMemo(() => {
    const types = new Set();
    let hasUntaggedEvents = false;

    events.forEach(event => {
      const tags = event.node.data.tags;

      if (tags && tags.length > 0) {
        tags.forEach(tagObj => {
          if (tagObj.event_type) {
            types.add(tagObj.event_type);
          }
        });
      } else {
        hasUntaggedEvents = true;
      }
    });

    // Add "Other Events" if there are untagged events
    if (hasUntaggedEvents) {
      types.add('Other Events');
    }

    // Sort filters but always put "Other Events" last
    const sortedTypes = Array.from(types)
      .filter(type => type !== 'Other Events' && type !== null && type !== undefined)
      .sort();

    if (types.has('Other Events')) {
      sortedTypes.push('Other Events');
    }

    return sortedTypes;
  }, [events]);

  // Initialize selectedFilters when availableFilters changes
  useEffect(() => {
    if (!isInitialized) return;

    // Only set defaults if selectedFilters is empty (no URL params were loaded)
    setSelectedFilters(prev => {
      // If filters were already set from URL, don't override them
      if (Object.keys(prev).length > 0) return prev;

      const newFilters = { ...prev };
      availableFilters.forEach(type => {
        if (!(type in newFilters)) {
          newFilters[type] = true;
        }
      });
      return newFilters;
    });
  }, [availableFilters, isInitialized]);

  // Filter events based on selected filters
  const filteredEvents = useMemo(() => {
    // Check if any filter is selected
    const hasSelectedFilter = Object.values(selectedFilters).some(v => v);
    if (!hasSelectedFilter) return [];

    return events.filter(event => {
      const tags = event.node.data.tags;

      // Handle "Other Events" filter for untagged events
      if (!tags || tags.length === 0) {
        return selectedFilters['Other Events'];
      }

      // Filter out null/undefined tags to get valid tags
      const validTags = tags.filter(tagObj => tagObj.event_type);
      
      // If no valid tags, treat as "Other Events"
      if (validTags.length === 0) {
        return selectedFilters['Other Events'];
      }

      // Check if any of the event's valid tags match selected filters
      return validTags.some(tagObj => selectedFilters[tagObj.event_type]);
    });
  }, [events, selectedFilters]);

  const handleFilterChange = filterType => {
    setSelectedFilters(prev => ({
      ...prev,
      [filterType]: !prev[filterType],
    }));
  };

  const handleSelectAll = () => {
    const newFilters = {};
    availableFilters.forEach(type => {
      newFilters[type] = true;
    });
    setSelectedFilters(newFilters);
  };

  const handleDeselectAll = () => {
    const newFilters = {};
    availableFilters.forEach(type => {
      newFilters[type] = false;
    });
    setSelectedFilters(newFilters);
  };

  return (
    <CalendarWrapper>
      <CalendarControls>
        {availableFilters.length > 0 && (
          <FilterSection>
            <FilterHeader>
              <FilterLabel>Event Type</FilterLabel>
              <FilterControls>
                <button
                  className="filter-button filter-button--select-all"
                  onClick={handleSelectAll}
                >
                  Select All
                </button>
                <span className="filter-divider">/</span>
                <button
                  className="filter-button filter-button--deselect-all"
                  onClick={handleDeselectAll}
                >
                  Deselect All
                </button>
              </FilterControls>
            </FilterHeader>

            <FilterPills>
              {availableFilters.map(filterType => (
                <label key={filterType} className="filter-pill">
                  <input
                    type="checkbox"
                    checked={selectedFilters[filterType] || false}
                    onChange={() => handleFilterChange(filterType)}
                  />
                  <span className="filter-label">{filterType}</span>
                </label>
              ))}
            </FilterPills>
          </FilterSection>
        )}

        <ViewSwitcher>
          <button
            onClick={() => setCurrentView('list')}
            className={`view-button view-button--list ${
              currentView === 'list' ? 'view-button--active' : ''
            }`}
          >
            {createTranslation('List', locale)}
          </button>
          <button
            onClick={() => setCurrentView('month')}
            className={`view-button view-button--month ${
              currentView === 'month' ? 'view-button--active' : ''
            }`}
          >
            {createTranslation('Month', locale)}
          </button>
        </ViewSwitcher>
      </CalendarControls>

      {(() => {
        // Check if any filter is selected
        const hasEventTypeFiltersSelected = Object.values(selectedFilters).some(
          v => v
        );

        return (
          <>
            <TransitionGroup component={null}>
              {currentView === 'list' && (
                <CSSTransition
                  timeout={200}
                  classNames="calendar-view-animation"
                >
                  <EventsListView
                    events={filteredEvents}
                    allEvents={events}
                    locale={locale}
                    selectedFilters={selectedFilters}
                    hasEventTypeFiltersSelected={hasEventTypeFiltersSelected}
                  />
                </CSSTransition>
              )}
            </TransitionGroup>

            <TransitionGroup component={null}>
              {currentView === 'month' && (
                <CSSTransition
                  timeout={200}
                  classNames="calendar-view-animation"
                >
                  <EventsMonthView
                    events={filteredEvents}
                    allEvents={events}
                    locale={locale}
                    selectedFilters={selectedFilters}
                    hasEventTypeFiltersSelected={hasEventTypeFiltersSelected}
                  />
                </CSSTransition>
              )}
            </TransitionGroup>
          </>
        );
      })()}
    </CalendarWrapper>
  );
};

export default Calendar;

/**
 * Styled Components
 *
 */
const CalendarWrapper = styled.div`
  position: relative;

  &:after {
    content: '';
    width: 100vw;
    height: 100%;
    position: absolute;
    top: -4.8rem;
    left: 50%;
    right: 50%;
    margin-left: -50vw;
    margin-right: -50vw;
    background-color: var(--color-off-white);
    z-index: -1;
  }

  .calendar-view-animation-enter {
    opacity: 0;
  }

  .calendar-view-animation-enter-active {
    opacity: 1;
    transition: all 300ms var(--ease-cubic-in-out);
  }

  .calendar-view-animation-exit {
    opacity: 1;
  }

  .calendar-view-animation-exit-active {
    opacity: 0;
    transition: all 300ms var(--ease-cubic-in-out);
  }
`;

const CalendarControls = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: var(--space-xl);
  gap: var(--space-xxl);
  justify-content: space-between;
  align-items: flex-end;
  border-bottom: 0.1rem solid rgb(225, 223, 223);
  padding-bottom: var(--space-m);
  background-color: var(--color-off-white);

  @media (${bp.min.sm}) {
    margin-top: 0;
  }
`;

const ViewSwitcher = styled.div`
  margin-left: auto;
  display: none;

  @media (${bp.min.sm}) {
    display: flex;
  }

  .view-button {
    padding: 0.4em 1em;
    cursor: pointer;
    outline: 0.1rem solid var(--color-neon-green);
    font-size: 1.6rem;
    min-width: 8rem;
    transition: all 100ms var(--ease-cubic-in-out);

    &--active {
      outline-color: var(--color-neon-green);
      background-color: var(--color-neon-green);
      z-index: 1;
    }

    &--list {
      border-radius: 0.4rem 0 0 0.4rem;
    }

    &--month {
      border-radius: 0 0.4rem 0.4rem 0;
    }
  }
`;

const FilterSection = styled.div`
  padding: 0;
  width: 100%;

  @media (${bp.min.sm}) {
    max-width: 84rem;
  }
`;

const FilterLabel = styled.label`
  display: block;
  font-size: 1.4rem;
  font-weight: var(--font-weight-semi-bold);
  color: var(--color-black);
  opacity: 0.75;
`;

const FilterHeader = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-s);
  margin-bottom: var(--space-xxs);
`;

const FilterControls = styled.div`
  display: flex;
  align-items: center;

  .filter-button {
    padding: 0;
    font-size: 1.4rem;
    border: none;
    background: transparent;
    cursor: pointer;
    transition: all 200ms var(--ease-cubic-in-out);
    color: var(--color-black);
    text-decoration: underline;
    font-weight: var(--font-weight-regular);

    &:hover {
      background-color: transparent;
      color: var(--color-rail-park-green);
    }
  }

  .filter-divider {
    color: var(--color-black);
    opacity: 0.5;
    margin: 0 var(--space-xxxs);
  }
`;

const FilterPills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-xs);

  .filter-pill {
    display: flex;
    align-items: center;
    padding: var(--space-xxxs) var(--space-xs);
    border: 0.1rem solid var(--color-black);
    border-radius: 10rem;
    cursor: pointer;
    transition: all 150ms var(--ease-cubic-in-out);
    user-select: none;
    background-color: #f5f5f5;
    color: var(--color-black);

    @media (hover: hover) {
      &:hover {
        opacity: 0.8;
      }
    }

    input {
      display: none;
    }

    .filter-label {
      font-size: 1.4rem;
      line-height: 1.4;
      cursor: pointer;
      color: var(--color-black);
    }

    &:hover {
      background-color: #ebebeb;
    }

    &:has(input:checked) {
      background-color: var(--color-black);
      border-color: var(--color-black);
    }

    &:has(input:checked) .filter-label {
      color: var(--color-white);
    }
  }
`;
